import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { fetchSurveySessionDetail } from "../api/client";

const repairDisplayText = (value) => {
  if (value == null || value === "") return "";
  const text = String(value);
  if (!/[ÃÂâ€]/.test(text)) return text.trim();
  try {
    return decodeURIComponent(escape(text)).trim();
  } catch {
    return text.trim();
  }
};

const formatAnswerText = (answer) => {
  if (!answer) return null;
  if (answer.optionLabel) return repairDisplayText(answer.optionLabel);
  if (answer.answerText) return repairDisplayText(answer.answerText);
  if (answer.answerNumber != null) return `Nota ${answer.answerNumber}`;
  if (answer.optionValue && !/^\d+$/.test(String(answer.optionValue))) {
    return repairDisplayText(String(answer.optionValue));
  }
  if (answer.answerScore != null) return String(answer.answerScore);
  return null;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SurveySessionDetailDialog = ({
  open,
  onClose,
  surveyId,
  sessionId,
  leadId = null,
  surveyTitle = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !surveyId || !sessionId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError("");
    setDetail(null);

    fetchSurveySessionDetail(surveyId, sessionId, leadId)
      .then((payload) => {
        if (cancelled) return;
        setDetail(payload || null);
        if (!payload?.session) setError("Resposta não encontrada.");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Não foi possível carregar as respostas.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, surveyId, sessionId, leadId]);

  const title =
    repairDisplayText(detail?.survey?.title || surveyTitle) || "Pesquisa respondida";
  const session = detail?.session;
  const answers = session?.answers || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {!loading && error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && !error && session ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {session.userName ? (
                <Chip size="small" label={`Respondido por ${repairDisplayText(session.userName)}`} />
              ) : null}
              {session.completedAt ? (
                <Chip size="small" variant="outlined" label={formatDateTime(session.completedAt)} />
              ) : null}
            </Stack>

            {answers.length ? (
              <Stack spacing={1.25}>
                {answers.map((answer, index) => {
                  const answerText = formatAnswerText(answer);
                  const isEmpty = !answerText;
                  return (
                    <Box
                      key={`survey-answer-${answer.questionId || index}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "background.default",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {repairDisplayText(answer.questionTitle) ||
                          (answer.questionPosition
                            ? `Pergunta ${answer.questionPosition}`
                            : "Pergunta")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: isEmpty ? 400 : 500,
                          fontStyle: isEmpty ? "italic" : "normal",
                          color: isEmpty ? "text.secondary" : "text.primary",
                        }}
                      >
                        {isEmpty ? "Não respondeu" : answerText}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma resposta registrada nesta sessão.
              </Typography>
            )}
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default SurveySessionDetailDialog;
